/**
 * downtime.utils.ts - Pokročilá logika pro výpočet odstávek (downtimes)
 * 
 * KLÍČOVÉ PROBLÉMY, KTERÉ TENTO MODUL ŘEŠÍ:
 * 
 * 1) PŘEKRYVY ODSTÁVEK:
 *    Pokud stroj má současně více aktivních odstávek (např. plánovaná + neplánovaná),
 *    nesmíme je sčítat přímo - získali bychom více minut/nákladů než ve skutečnosti.
 *    
 *    Příklad: Odstávka A: 10:00-12:00, Odstávka B: 11:00-13:00
 *      - Naivní součet: 2h + 2h = 4h (ŠPATNĚ!)
 *      - Sjednocení: 10:00-13:00 = 3h (SPRÁVNĚ)
 * 
 * 2) SAZBY V PŘEKRYVU:
 *    Při překryvu více odstávek s různými hodinovými sazbami potřebujeme
 *    v každém okamžiku použít nejvyšší aktivní sazbu.
 *    
 *    Příklad: Odstávka A (100 Kč/h): 10:00-12:00, Odstávka B (200 Kč/h): 11:00-13:00
 *      - 10:00-11:00: 100 Kč/h (jen A)
 *      - 11:00-12:00: 200 Kč/h (A+B, max = 200)
 *      - 12:00-13:00: 200 Kč/h (jen B)
 *      - Celkem: 100*1 + 200*1 + 200*1 = 500 Kč
 * 
 * 3) AGREGACE PO DNECH/MĚSÍCÍCH:
 *    Dlouhé odstávky přesahující půlnoc musíme rozdělit do správných dnů/měsíců.
 * 
 * POUŽITÝ ALGORITMUS: SWEEP LINE (zametací přímka)
 *    - Převedeme intervaly na eventy (start +1, end -1)
 *    - Seřadíme podle času
 *    - Procházíme zleva doprava a udržujeme počet aktivních intervalů
 *    - Když aktivní > 0, přičítáme čas/náklady
 * 
 * OPTIMALIZACE:
 *    - Pro výkon nepoužíváme moment.js - vše v čistých Date/timestamp operacích
 *    - Multiset sazeb s cached maximem - O(1) pro získání max sazby
 *    - Předpočítané union intervaly pro opakované dotazy
 * 
 * Pozn.: pro výkon v horkých cestách nepoužíváme moment – vše je počítáno v ms (lokální čas)
 */

import {
    startOfProductionDayMs,
    endOfProductionDayMs,
    productionDayKey,
} from './production-day.utils';

type AnyItem = any;

function getWorkplaceId(item: AnyItem): string | null {
    return item?.workplaceId ?? item?.workplace?.id ?? null;
}

/**
 * Vrací konec odstávky (shutdownToDate).
 * Pokud je null (odstávka ještě není ukončená), vrací aktuální čas v ISO formátu.
 * @param item Odstávka (PlanItem)
 * @returns ISO string konce odstávky nebo aktuálního času
 */
function getShutdownEndDate(item: AnyItem): string | null {
    const shutdownToDate = item?.shutdownToDate;
    if (shutdownToDate != null && typeof shutdownToDate === 'string' && shutdownToDate.trim() !== '') {
        return shutdownToDate;
    }
    // Pokud shutdownToDate je null/undefined/prázdný, odstávka ještě běží → použijeme aktuální čas
    return new Date().toISOString();
}

function parseMs(iso?: string | null): number | null {
    if (!iso) return null;
    const ms = Date.parse(iso);
    return isNaN(ms) ? null : ms;
}

function clipToRange(startIso?: string | null, endIso?: string | null, fromIso?: string, toIso?: string): { fromMs: number; toMs: number } | null {
    const sMs0 = parseMs(startIso);
    const eMs0 = parseMs(endIso);
    if (sMs0 == null || eMs0 == null) return null;
    let sMs = sMs0;
    let eMs = eMs0;
    const fMs = parseMs(fromIso);
    const tMs = parseMs(toIso);
    if (fMs != null && sMs < fMs) sMs = fMs;
    if (tMs != null && eMs > tMs) eMs = tMs;
    if (eMs <= sMs) return null;
    return { fromMs: sMs, toMs: eMs };
}

function pad2(n: number): string { return n < 10 ? `0${n}` : `${n}`; }
function dayKeyFromMs(ms: number): string {
    // Používáme productionDayKey pro správné přiřazení k výrobnímu dni
    return productionDayKey(ms);
}
function monthKeyFromMs(ms: number): string {
    const d = new Date(ms);
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-01`;
}
function startOfDayLocalMs(ms: number): number {
    // Používáme startOfProductionDayMs pro začátek výrobního dne
    return startOfProductionDayMs(ms);
}
function startOfNextDayLocalMs(ms: number): number {
    // Používáme endOfProductionDayMs pro konec výrobního dne
    return endOfProductionDayMs(ms);
}
function startOfMonthLocalMs(ms: number): number {
    const d = new Date(ms);
    return new Date(d.getFullYear(), d.getMonth(), 1).getTime();
}
function startOfNextMonthLocalMs(ms: number): number {
    const d = new Date(ms);
    return new Date(d.getFullYear(), d.getMonth() + 1, 1).getTime();
}

/**
 * Multiset sazeb s cache aktuálního maxima.
 * 
 * Účel: Efektivní správa množiny sazeb, kde se prvky mohou opakovat,
 * a rychlé získání maximální hodnoty.
 * 
 * Výhoda oproti Array.max(): O(1) pro získání maxima (namísto O(n))
 * 
 * Použití: Při sweep-line algoritmu přidáváme/odebíráme sazby podle
 * start/end eventů a potřebujeme znát aktuální maximum v O(1).
 * 
 * @returns Objekt s metodami add, remove, hasAny, max
 */
function createRateMultiset() {
    const counts = new Map<number, number>(); // rate → počet výskytů
    let cachedMax = 0; // cache aktuálního maxima
    
    // Přepočítá maximum procházením všech klíčů
    function recalcMax() {
        let m = 0;
        for (const r of counts.keys()) if (r > m) m = r;
        cachedMax = m;
    }
    
    return {
        // Přidá sazbu (zvýší počítadlo)
        add(rate: number) {
            const c = (counts.get(rate) || 0) + 1;
            counts.set(rate, c);
            // Pokud je nová sazba větší než dosavadní max, update cache
            if (rate > cachedMax) cachedMax = rate;
        },
        // Odebere sazbu (sníží počítadlo)
        remove(rate: number) {
            const c = (counts.get(rate) || 0) - 1;
            if (c <= 0) {
                counts.delete(rate);
                // Pokud jsme odstranili aktuální maximum, musíme přepočítat
                if (rate === cachedMax) recalcMax();
            } else {
                counts.set(rate, c);
            }
        },
        // Jsou nějaké aktivní sazby?
        hasAny(): boolean { return counts.size > 0; },
        // Vrátí maximum (O(1) díky cache)
        max(): number { return cachedMax; },
    };
}

/**
 * Vypočítá sjednocené minuty odstávek po pracovištích.
 * 
 * SWEEP-LINE ALGORITMUS:
 * 1. Seskupíme odstávky podle workplaceId
 * 2. Pro každé pracoviště:
 *    a) Vytvoříme eventy: start interval → +1, end interval → -1
 *    b) Seřadíme eventy podle času (při rovnosti start před end)
 *    c) Procházíme zleva doprava a udržujeme počet aktivních intervalů
 *    d) Když active > 0, přičítáme čas mezi eventy
 * 
 * Příklad: Odstávka A: 10:00-12:00, Odstávka B: 11:00-13:00
 *   Eventy: [(10:00, +1), (11:00, +1), (12:00, -1), (13:00, -1)]
 *   Sweep:
 *     10:00: active=0→1, prev=10:00
 *     11:00: (cur-prev)*active = 1h*1, active=1→2, prev=11:00
 *     12:00: (cur-prev)*active = 1h*2, active=2→1, prev=12:00
 *     13:00: (cur-prev)*active = 1h*1, active=1→0
 *   Celkem: 1h + 1h + 1h = 3h (správně!)
 * 
 * Poznámka: Před výpočtem ořežeme intervaly do zadaného rozsahu [from, to].
 * 
 * @param items Pole odstávek (PlanItem s shutdownFromDate/shutdownToDate)
 * @param fromIso Dolní hranice rozsahu (volitelné)
 * @param toIso Horní hranice rozsahu (volitelné)
 * @returns Mapa workplaceId → minuty sjednocené odstávky
 */
export function computeUnionMinutesByWorkplace(items: AnyItem[], fromIso?: string, toIso?: string): Map<string | null, number> {
    // Fáze 1: Seskupení podle pracoviště
    const byWp = new Map<string | null, { fromMs: number; toMs: number }[]>();
    for (const it of items || []) {
        // Ořežeme interval do rozsahu [from, to]
        const interval = clipToRange(it?.shutdownFromDate, getShutdownEndDate(it), fromIso, toIso);
        if (!interval) continue;
        const wid = getWorkplaceId(it);
        const arr = byWp.get(wid) || [];
        arr.push(interval);
        byWp.set(wid, arr);
    }
    
    // Fáze 2: Pro každé pracoviště spočítáme union pomocí sweep-line
    const result = new Map<string | null, number>();
    for (const [wid, intervals] of byWp.entries()) {
        type E = { ts: number; k: 1 | -1 }; // event: timestamp, kind (+1=start, -1=end)
        const events: E[] = [];
        
        // Vytvoříme eventy pro všechny intervaly
        for (const iv of intervals) {
            events.push({ ts: iv.fromMs, k: 1 });   // start
            events.push({ ts: iv.toMs, k: -1 });    // end
        }
        
        if (events.length === 0) { result.set(wid, 0); continue; }
        
        // Seřadíme podle času (při rovnosti: start před end)
        events.sort((a, b) => (a.ts === b.ts ? a.k - b.k : a.ts - b.ts));
        
        // Sweep-line: procházíme zleva doprava
        let active = 0;      // počet aktuálně aktivních intervalů
        let prev = events[0].ts;  // předchozí časový bod
        let minutes = 0;
        
        for (let i = 0; i < events.length; ) {
            const cur = events[i].ts;
            
            // Pokud jsou nějaké aktivní intervaly, přičteme čas
            if (cur > prev && active > 0) {
                minutes += (cur - prev) / 60000;
            }
            
            // Zpracujeme všechny eventy se stejným časem
            let j = i;
            while (j < events.length && events[j].ts === cur) {
                active += events[j].k;  // +1 pro start, -1 pro end
                j++;
            }
            
            prev = cur;
            i = j;
        }
        
        result.set(wid, minutes);
    }
    return result;
}

export function computeUnionMinutesTotal(items: AnyItem[], fromIso?: string, toIso?: string): number {
    const map = computeUnionMinutesByWorkplace(items, fromIso, toIso);
    let sum = 0;
    for (const v of map.values()) sum += v;
    return sum;
}

/**
 * Předpočítá sjednocené (nepřekrývající se) intervaly po `workplaceId` v daném rozsahu – pro opakované dotazy na překryv.
 * Výhoda: následné výpočty překryvů pro mnoho menších intervalů (např. směn) jsou O(k) s binárním vyhledáním startu.
 */
export function buildUnionIntervalsByWorkplace(items: AnyItem[], fromIso?: string, toIso?: string): Map<string | null, { fromMs: number; toMs: number }[]> {
    const byWp = new Map<string | null, { fromMs: number; toMs: number }[]>();
    // Připrav eventy a pak vygeneruj sjednocené segmenty jako v computeUnionMinutesByWorkplace
    const eventsByWp = new Map<string | null, { ts: number; k: 1 | -1 }[]>();
    for (const it of items || []) {
        const iv = clipToRange(it?.shutdownFromDate, getShutdownEndDate(it), fromIso, toIso);
        if (!iv) continue;
        const wid = getWorkplaceId(it);
        const arr = eventsByWp.get(wid) || [];
        arr.push({ ts: iv.fromMs, k: 1 });
        arr.push({ ts: iv.toMs, k: -1 });
        eventsByWp.set(wid, arr);
    }
    for (const [wid, events] of eventsByWp.entries()) {
        if ((events?.length || 0) === 0) { byWp.set(wid, []); continue; }
        events.sort((a, b) => (a.ts === b.ts ? a.k - b.k : a.ts - b.ts));
        let active = 0;
        let prev = events[0].ts;
        const merged: { fromMs: number; toMs: number }[] = [];
        for (let i = 0; i < events.length; ) {
            const cur = events[i].ts;
            if (cur > prev && active > 0) {
                // segment [prev, cur)
                const last = merged[merged.length - 1];
                if (last && last.toMs === prev) last.toMs = cur; else merged.push({ fromMs: prev, toMs: cur });
            }
            let j = i;
            while (j < events.length && events[j].ts === cur) { active += events[j].k; j++; }
            prev = cur;
            i = j;
        }
        byWp.set(wid, merged);
    }
    return byWp;
}

/**
 * Vrátí minuty překryvu sjednocených (nepřekrývajících) intervalů s intervalem [fromMs, toMs).
 * Kolekce `merged` musí být vzestupně podle `fromMs` a bez překryvů – ideálně z `buildUnionIntervalsByWorkplace`.
 */
export function sumOverlapMinutes(merged: { fromMs: number; toMs: number }[] | undefined, fromMs: number, toMs: number): number {
    if (!merged || merged.length === 0 || toMs <= fromMs) return 0;
    // Najdi první interval s toMs > fromMs (lower_bound)
    let lo = 0, hi = merged.length - 1, idx = merged.length;
    while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (merged[mid].toMs > fromMs) { idx = mid; hi = mid - 1; } else { lo = mid + 1; }
    }
    let minutes = 0;
    for (let i = idx; i < merged.length; i++) {
        const iv = merged[i];
        if (iv.fromMs >= toMs) break;
        const s = Math.max(fromMs, iv.fromMs);
        const e = Math.min(toMs, iv.toMs);
        if (e > s) minutes += (e - s) / 60000;
    }
    return minutes;
}

/**
 * Vypočítá průnik dvou sad intervalů (v minutách) omezený na daný rozsah.
 * 
 * Použití: Když potřebujeme odečíst překryv plánovaných a neplánovaných odstávek.
 * Pokud je stroj v plánované odstávce, nemůže být zároveň v neplánované.
 * 
 * Příklad:
 * - Plánovaná: 10:00-11:00 (60 min)
 * - Neplánovaná: 10:30-11:30 (60 min)
 * - Průnik: 10:30-11:00 (30 min) ← tento čas musíme odečíst z neplánované
 * 
 * @param intervals1 První sada sjednocených intervalů
 * @param intervals2 Druhá sada sjednocených intervalů
 * @param fromMs Dolní hranice rozsahu (ms)
 * @param toMs Horní hranice rozsahu (ms)
 * @returns Minuty průniku obou sad v daném rozsahu
 */
export function computeIntervalIntersectionMinutes(
    intervals1: { fromMs: number; toMs: number }[] | undefined,
    intervals2: { fromMs: number; toMs: number }[] | undefined,
    fromMs: number,
    toMs: number
): number {
    if (!intervals1 || intervals1.length === 0 || !intervals2 || intervals2.length === 0 || toMs <= fromMs) {
        return 0;
    }
    
    let totalMinutes = 0;
    
    // Pro každý interval z první sady
    for (const iv1 of intervals1) {
        // Pokud interval je mimo rozsah, skip
        if (iv1.toMs <= fromMs || iv1.fromMs >= toMs) continue;
        
        // Ořežeme interval do rozsahu
        const iv1Start = Math.max(iv1.fromMs, fromMs);
        const iv1End = Math.min(iv1.toMs, toMs);
        
        // Hledáme průniky s intervaly z druhé sady
        for (const iv2 of intervals2) {
            // Pokud iv2 je před iv1, skip
            if (iv2.toMs <= iv1Start) continue;
            // Pokud iv2 je za iv1, můžeme skončit (intervaly jsou seřazené)
            if (iv2.fromMs >= iv1End) break;
            
            // Ořežeme iv2 do rozsahu
            const iv2Start = Math.max(iv2.fromMs, fromMs);
            const iv2End = Math.min(iv2.toMs, toMs);
            
            // Spočítáme průnik iv1 ∩ iv2
            const intersectStart = Math.max(iv1Start, iv2Start);
            const intersectEnd = Math.min(iv1End, iv2End);
            
            if (intersectEnd > intersectStart) {
                totalMinutes += (intersectEnd - intersectStart) / 60000;
            }
        }
    }
    
    return totalMinutes;
}

export function computeUnionCostsByWorkplace(items: AnyItem[], hourlySelector: (item: AnyItem) => number, fromIso?: string, toIso?: string): Map<string | null, number> {
    const byWp = new Map<string | null, { fromMs: number; toMs: number; rate: number }[]>();
    for (const it of items || []) {
        const interval = clipToRange(it?.shutdownFromDate, getShutdownEndDate(it), fromIso, toIso);
        if (!interval) continue;
        const rate = Number(hourlySelector(it) || 0) || 0;
        const wid = getWorkplaceId(it);
        const arr = byWp.get(wid) || [];
        arr.push({ ...interval, rate });
        byWp.set(wid, arr);
    }
    const result = new Map<string | null, number>();
    for (const [wid, intervals] of byWp.entries()) {
        type E = { ts: number; k: 1 | -1; rate: number };
        const events: E[] = [];
        for (const iv of intervals) {
            events.push({ ts: iv.fromMs, k: 1, rate: iv.rate });
            events.push({ ts: iv.toMs, k: -1, rate: iv.rate });
        }
        if (events.length === 0) { result.set(wid, 0); continue; }
        events.sort((a, b) => (a.ts === b.ts ? a.k - b.k : a.ts - b.ts));
        const rates = createRateMultiset();
        let prev = events[0].ts;
        let kc = 0;
        for (let i = 0; i < events.length; ) {
            const cur = events[i].ts;
            if (cur > prev && rates.hasAny()) {
                const minutes = (cur - prev) / 60000;
                const maxRate = rates.max();
                kc += (maxRate / 60) * minutes;
            }
            let j = i;
            while (j < events.length && events[j].ts === cur) {
                const e = events[j];
                if (e.k === 1) rates.add(e.rate); else rates.remove(e.rate);
                j++;
            }
            prev = cur;
            i = j;
        }
        result.set(wid, kc);
    }
    return result;
}

export function computeUnionCostsTotal(items: AnyItem[], hourlySelector: (item: AnyItem) => number, fromIso?: string, toIso?: string): number {
    const map = computeUnionCostsByWorkplace(items, hourlySelector, fromIso, toIso);
    let sum = 0;
    for (const v of map.values()) sum += v;
    return sum;
}

/**
 * Rychlá denní agregace union nákladů: jednorázový sweep po pracovištích, rozpad do YYYY-MM-DD.
 */
export function computeUnionCostsDailyTotal(items: AnyItem[], hourlySelector: (item: AnyItem) => number, fromIso: string, toIso: string): Map<string, number> {
    const daily = new Map<string, number>();
    const byWp = new Map<string | null, { fromMs: number; toMs: number; rate: number }[]>();
    for (const it of items || []) {
        const interval = clipToRange(it?.shutdownFromDate, getShutdownEndDate(it), fromIso, toIso);
        if (!interval) continue;
        const rate = Number(hourlySelector(it) || 0) || 0;
        const wid = getWorkplaceId(it);
        const arr = byWp.get(wid) || [];
        arr.push({ ...interval, rate });
        byWp.set(wid, arr);
    }
    for (const [, intervals] of byWp.entries()) {
        type E = { ts: number; k: 1 | -1; rate: number };
        const events: E[] = [];
        for (const iv of intervals) {
            events.push({ ts: iv.fromMs, k: 1, rate: iv.rate });
            events.push({ ts: iv.toMs, k: -1, rate: iv.rate });
        }
        if (events.length === 0) continue;
        events.sort((a, b) => (a.ts === b.ts ? a.k - b.k : a.ts - b.ts));
        const rates = createRateMultiset();
        let prev = events[0].ts;
        for (let i = 0; i < events.length; ) {
            const cur = events[i].ts;
            if (cur > prev && rates.hasAny()) {
                const rate = rates.max();
                const perMin = rate / 60;
                let segStart = prev;
                const segEnd = cur;
                while (segStart < segEnd) {
                    const dayKey = dayKeyFromMs(segStart);
                    const nextDayStart = startOfNextDayLocalMs(segStart);
                    const sliceEnd = Math.min(segEnd, nextDayStart);
                    const minutes = (sliceEnd - segStart) / 60000;
                    daily.set(dayKey, (daily.get(dayKey) || 0) + perMin * minutes);
                    segStart = sliceEnd;
                }
            }
            let j = i;
            while (j < events.length && events[j].ts === cur) {
                const e = events[j];
                if (e.k === 1) rates.add(e.rate); else rates.remove(e.rate);
                j++;
            }
            prev = cur;
            i = j;
        }
    }
    return daily;
}

/**
 * Rychlá měsíční agregace union nákladů: jednorázový sweep po pracovištích, rozpad do YYYY-MM-01.
 */
export function computeUnionCostsMonthlyTotal(items: AnyItem[], hourlySelector: (item: AnyItem) => number, fromIso: string, toIso: string): Map<string, number> {
    const monthly = new Map<string, number>();
    const byWp = new Map<string | null, { fromMs: number; toMs: number; rate: number }[]>();
    for (const it of items || []) {
        const interval = clipToRange(it?.shutdownFromDate, getShutdownEndDate(it), fromIso, toIso);
        if (!interval) continue;
        const rate = Number(hourlySelector(it) || 0) || 0;
        const wid = getWorkplaceId(it);
        const arr = byWp.get(wid) || [];
        arr.push({ ...interval, rate });
        byWp.set(wid, arr);
    }
    for (const [, intervals] of byWp.entries()) {
        type E = { ts: number; k: 1 | -1; rate: number };
        const events: E[] = [];
        for (const iv of intervals) {
            events.push({ ts: iv.fromMs, k: 1, rate: iv.rate });
            events.push({ ts: iv.toMs, k: -1, rate: iv.rate });
        }
        if (events.length === 0) continue;
        events.sort((a, b) => (a.ts === b.ts ? a.k - b.k : a.ts - b.ts));
        const rates = createRateMultiset();
        let prev = events[0].ts;
        for (let i = 0; i < events.length; ) {
            const cur = events[i].ts;
            if (cur > prev && rates.hasAny()) {
                const rate = rates.max();
                const perMin = rate / 60;
                let segStart = prev;
                const segEnd = cur;
                while (segStart < segEnd) {
                    const mKey = monthKeyFromMs(segStart);
                    const nextMonthStart = startOfNextMonthLocalMs(segStart);
                    const sliceEnd = Math.min(segEnd, nextMonthStart);
                    const minutes = (sliceEnd - segStart) / 60000;
                    monthly.set(mKey, (monthly.get(mKey) || 0) + perMin * minutes);
                    segStart = sliceEnd;
                }
            }
            let j = i;
            while (j < events.length && events[j].ts === cur) {
                const e = events[j];
                if (e.k === 1) rates.add(e.rate); else rates.remove(e.rate);
                j++;
            }
            prev = cur;
            i = j;
        }
    }
    return monthly;
}

/**
 * Rychlá denní agregace union minut: jednorázový sweep po pracovištích, rozpad do YYYY-MM-DD.
 */
export function computeUnionMinutesDailyTotal(items: AnyItem[], fromIso: string, toIso: string): Map<string, number> {
    const daily = new Map<string, number>();
    const byWp = new Map<string | null, { fromMs: number; toMs: number }[]>();
    for (const it of items || []) {
        const interval = clipToRange(it?.shutdownFromDate, getShutdownEndDate(it), fromIso, toIso);
        if (!interval) continue;
        const wid = getWorkplaceId(it);
        const arr = byWp.get(wid) || [];
        arr.push(interval);
        byWp.set(wid, arr);
    }
    for (const [, intervals] of byWp.entries()) {
        type E = { ts: number; k: 1 | -1 };
        const events: E[] = [];
        for (const iv of intervals) { events.push({ ts: iv.fromMs, k: 1 }); events.push({ ts: iv.toMs, k: -1 }); }
        if (events.length === 0) continue;
        events.sort((a, b) => (a.ts === b.ts ? a.k - b.k : a.ts - b.ts));
        let active = 0;
        let prev = events[0].ts;
        for (let i = 0; i < events.length; ) {
            const cur = events[i].ts;
            if (cur > prev && active > 0) {
                let segStart = prev;
                const segEnd = cur;
                while (segStart < segEnd) {
                    const dayKey = dayKeyFromMs(segStart);
                    const nextDayStart = startOfNextDayLocalMs(segStart);
                    const sliceEnd = Math.min(segEnd, nextDayStart);
                    const minutes = (sliceEnd - segStart) / 60000;
                    daily.set(dayKey, (daily.get(dayKey) || 0) + minutes);
                    segStart = sliceEnd;
                }
            }
            let j = i;
            while (j < events.length && events[j].ts === cur) { active += events[j].k; j++; }
            prev = cur;
            i = j;
        }
    }
    return daily;
}

/**
 * Jediný průchod pro dvě nezávislé nákladové sazby (např. downtime a lost profit)
 * a současné denní i měsíční agregace.
 *
 * Proč: minimalizace práce v horké cestě –
 *  - jednou sestavíme intervaly a eventy
 *  - jedním sweep-line průchodem spočteme obě sazby zároveň
 *  - segmenty rovnou rozřežeme do dnů i měsíců a inkrementujeme mapy
 */
export function computeUnionTwoCostsDailyMonthlyTotal(
    items: AnyItem[],
    hourlySelectorA: (item: AnyItem) => number,
    hourlySelectorB: (item: AnyItem) => number,
    fromIso: string,
    toIso: string
): { dailyA: Map<string, number>; monthlyA: Map<string, number>; totalA: number; dailyB: Map<string, number>; monthlyB: Map<string, number>; totalB: number } {
    const dailyA = new Map<string, number>();
    const monthlyA = new Map<string, number>();
    const dailyB = new Map<string, number>();
    const monthlyB = new Map<string, number>();
    let totalA = 0;
    let totalB = 0;

    // 1) Připravíme intervaly s oběma sazbami po pracovištích
    const byWp = new Map<string | null, { fromMs: number; toMs: number; rateA: number; rateB: number }[]>();
    for (const it of items || []) {
        const iv = clipToRange(it?.shutdownFromDate, getShutdownEndDate(it), fromIso, toIso);
        if (!iv) continue;
        const rateA = Number(hourlySelectorA(it) || 0) || 0;
        const rateB = Number(hourlySelectorB(it) || 0) || 0;
        if (rateA <= 0 && rateB <= 0) continue; // zcela bez nákladů – vynecháme
        const wid = getWorkplaceId(it);
        const arr = byWp.get(wid) || [];
        arr.push({ ...iv, rateA, rateB });
        byWp.set(wid, arr);
    }

    // 2) Pro každé pracoviště sestavíme eventy a provedeme sweep-line
    for (const [, intervals] of byWp.entries()) {
        type E = { ts: number; k: 1 | -1; rateA: number; rateB: number };
        const events: E[] = [];
        for (const iv of intervals) {
            events.push({ ts: iv.fromMs, k: 1, rateA: iv.rateA, rateB: iv.rateB });
            events.push({ ts: iv.toMs, k: -1, rateA: iv.rateA, rateB: iv.rateB });
        }
        if (events.length === 0) continue;
        events.sort((a, b) => (a.ts === b.ts ? a.k - b.k : a.ts - b.ts));

        // Multisety pro obě sazby, nezávislé maxima v každém momentu
        const ratesA = createRateMultiset();
        const ratesB = createRateMultiset();
        let prev = events[0].ts;
        for (let i = 0; i < events.length; ) {
            const cur = events[i].ts;
            if (cur > prev && (ratesA.hasAny() || ratesB.hasAny())) {
                const perMinA = ratesA.hasAny() ? (ratesA.max() / 60) : 0;
                const perMinB = ratesB.hasAny() ? (ratesB.max() / 60) : 0;
                let segStart = prev;
                const segEnd = cur;
                // 3) Segment rozřežeme dle hranic dnů i měsíců najednou: krokujeme po nejbližší hranici
                while (segStart < segEnd) {
                    const nextDay = startOfNextDayLocalMs(segStart);
                    const nextMonth = startOfNextMonthLocalMs(segStart);
                    const nextBoundary = Math.min(nextDay, nextMonth);
                    const sliceEnd = Math.min(segEnd, nextBoundary);
                    const minutes = (sliceEnd - segStart) / 60000;
                    if (minutes > 0) {
                        const dKey = dayKeyFromMs(segStart);
                        const mKey = monthKeyFromMs(segStart);
                        if (perMinA > 0) {
                            const addA = perMinA * minutes;
                            dailyA.set(dKey, (dailyA.get(dKey) || 0) + addA);
                            monthlyA.set(mKey, (monthlyA.get(mKey) || 0) + addA);
                            totalA += addA;
                        }
                        if (perMinB > 0) {
                            const addB = perMinB * minutes;
                            dailyB.set(dKey, (dailyB.get(dKey) || 0) + addB);
                            monthlyB.set(mKey, (monthlyB.get(mKey) || 0) + addB);
                            totalB += addB;
                        }
                    }
                    segStart = sliceEnd;
                }
            }
            // 4) Aplikujeme všechny eventy s aktuálním časem: starty přidají sazby, konce odeberou
            let j = i;
            while (j < events.length && events[j].ts === cur) {
                const e = events[j];
                if (e.k === 1) { ratesA.add(e.rateA); ratesB.add(e.rateB); } else { ratesA.remove(e.rateA); ratesB.remove(e.rateB); }
                j++;
            }
            prev = cur;
            i = j;
        }
    }

    return { dailyA, monthlyA, totalA, dailyB, monthlyB, totalB };
}


